import type { StateCreator } from "zustand";
import { normalizeBuildProgress } from "../domain/build/buildProgress";
import { DEFAULT_FINISHES } from "../domain/finishes/types";
import type { WallOpening } from "../domain/framing/generateFramedWall";
import { resolveWallHeightM } from "../domain/geometry/buildingDimensions";
import { normalizeIndoorRoom } from "../domain/indoor/indoorRoom";
import { DEFAULT_INTL_CLIMATE_PROFILE } from "../domain/location/types";
import { validateOpeningSet } from '../domain/openings/validateOpeningSet';
import {
	DEFAULT_ROOF_CONFIG_BY_TYPE,
	normalizeRoofConfig,
} from "../domain/roof/roofConfig";
import {
	DEFAULT_SAUNA_ROTATION_DEG,
	snapRotationDeg,
	stepRotationDeg,
} from "../domain/viewer/saunaRotation";
import {
	DEFAULT_TIME_OF_DAY,
	normalizeTimeOfDay,
} from "../domain/viewer/timeOfDay";
import { withoutPanoramaWallWindows } from "../engines/twoRoomOpeningConfig";
import { resolveTwoRoomConfig } from "../engines/twoRoomSauna";
import type { SaunaDimensions } from "../engines/types";
import { getT } from "../i18n";
import { formatOpeningIssue } from '../i18n/formatOpeningIssue';
import {
	DEFAULT_SITE_CONDITIONS,
	DEFAULT_THERMAL,
	DEFAULT_VIS,
} from "../pages/builder/constants";
import {
	DEFAULT_DIVIDER_DOOR_ID,
	DEFAULT_FRONT_DOOR_ID,
	DEFAULT_RELAX_WINDOW_ID,
	getCenteredDividerDoorX,
	getDefaultFrontDoorX,
	getDefaultOpenings,
	getRelaxWindowX,
} from "../pages/builder/defaultOpenings";
import { fromDesignRow } from "../pages/builder/mappers";
import { setStatusWithoutHistory } from "./builderStorePersistence";
import { applyTwoRoomPresetLook, TWO_ROOM_PRESETS } from "./twoRoomPresets";
import type { BuilderStore } from "./useBuilderStore";

export function recenterSeededDefaultOpenings(
	openings: WallOpening[],
	prevDimensions: SaunaDimensions,
	nextDimensions: SaunaDimensions,
): WallOpening[] {
	const prevWidthMm = Math.round(prevDimensions.width * 1000);
	const nextWidthMm = Math.round(nextDimensions.width * 1000);

	return openings.map((opening) => {
		if (opening.id === DEFAULT_FRONT_DOOR_ID && opening.wall === "front") {
			const previousDefaultX = getDefaultFrontDoorX(
				prevWidthMm,
				opening.widthMm,
			);
			if (opening.xMm !== previousDefaultX) {
				return opening;
			}

			return {
				...opening,
				xMm: getDefaultFrontDoorX(nextWidthMm, opening.widthMm),
			};
		}

		if (opening.id === DEFAULT_DIVIDER_DOOR_ID && opening.wall === "divider") {
			const previousDefaultX = getCenteredDividerDoorX(
				prevWidthMm,
				opening.widthMm,
			);
			if (opening.xMm !== previousDefaultX) {
				return opening;
			}

			return {
				...opening,
				xMm: getCenteredDividerDoorX(nextWidthMm, opening.widthMm),
			};
		}

		if (opening.id === DEFAULT_RELAX_WINDOW_ID && opening.wall === "right") {
			const prevLengthMm = Math.round(prevDimensions.length * 1000);
			const nextLengthMm = Math.round(nextDimensions.length * 1000);
			const previousDefaultX = getRelaxWindowX(prevLengthMm, opening.widthMm);
			if (opening.xMm !== previousDefaultX) {
				return opening;
			}

			return {
				...opening,
				xMm: getRelaxWindowX(nextLengthMm, opening.widthMm),
			};
		}

		return opening;
	});
}

type StoreSet = Parameters<StateCreator<BuilderStore>>[0];

export function createDraftActions(set: StoreSet): Pick<
	BuilderStore,
	| "setDimensions"
	| "setPartitionSplit"
	| "setTwoRoomConfig"
		| "setTwoRoomPreset"
		| "setTimeOfDay"
		| "setRotationDeg"
		| "stepRotation"
		| "setWizardCapacityHint"
	| "setVisibility"
	| "setOpenings"
	| "setDeckConfig"
	| "setRoofConfig"
	| "setBuildProgress"
	| "setThermalInputs"
	| "setSiteConditions"
	| "setInteriorLayout"
	| "setFinishes"
	| "setWallHeightM"
	| "setIndoorRoom"
	| "setProjectName"
	| "setLocationMode"
	| "setClimateProfile"
	| "setPostalCode"
	| "hydrateFromDesign"
	| "resetToDefaults"
	| "loadTemplate"
> {
	return {
		setDimensions: (d) =>
			set((state) => ({
				dimensions: d,
				// A manual resize invalidates any wizard capacity promise — the
				// room this many-people claim was made for no longer exists. Only
				// clear on an ACTUAL change so an equal-value no-op call (e.g. a
				// subscription re-firing) doesn't drop a hint that still applies.
				wizardCapacityHint:
					d.width === state.dimensions.width &&
					d.length === state.dimensions.length
						? state.wizardCapacityHint
						: null,
				openings: recenterSeededDefaultOpenings(
					state.openings,
					state.dimensions,
					d,
				),
			})),
		setPartitionSplit: (partitionSplit) => set(state => {
      const result = validateOpeningSet({dimensions:state.dimensions,wallHeightM:state.wallHeightM ?? undefined,openings:state.openings,partitionSplit,...state.twoRoomConfig,roofConfig:state.roofConfig});
      const collision = result.issues.find(i=>i.detail==='partition_collision');
      return collision ? {saveStatus:formatOpeningIssue(collision,getT(state.market))} : {partitionSplit};
    }),
		setTwoRoomConfig: (config) =>
			set((state) => {
				const twoRoomConfig = resolveTwoRoomConfig({
					...state.twoRoomConfig,
					...config,
				});
				return {
					twoRoomConfig,
					openings: withoutPanoramaWallWindows(state.openings, twoRoomConfig),
				};
			}),
			setTwoRoomPreset: (preset) =>
				set((state) => {
					const twoRoomConfig = resolveTwoRoomConfig(TWO_ROOM_PRESETS[preset]);
					return {
						twoRoomConfig,
						openings: withoutPanoramaWallWindows(state.openings, twoRoomConfig),
						...applyTwoRoomPresetLook(preset, state),
					};
				}),
			setTimeOfDay: (timeOfDay) =>
				set({ timeOfDay: normalizeTimeOfDay(timeOfDay) }),
			setRotationDeg: (deg) => set({ rotationDeg: snapRotationDeg(deg) }),
			stepRotation: (steps) =>
				set((state) => ({
					rotationDeg: stepRotationDeg(state.rotationDeg, steps),
				})),
			setWizardCapacityHint: (hint) => set({ wizardCapacityHint: hint }),
			setVisibility: (v) => set({ visibility: v }),
		setOpenings: (v) =>
			set((state) => ({
				openings: typeof v === "function" ? v(state.openings) : v,
			})),
		setDeckConfig: (deckConfig) => set({ deckConfig }),
		setRoofConfig: (roofConfig) => set({ roofConfig }),
		// buildProgress lives outside partialize, so its history entry can never
		// be undone — but an unguarded set() still clears zundo's futureStates.
		// Write it untracked, like the persistence status flags.
		setBuildProgress: (v) =>
			setStatusWithoutHistory(() =>
				set((state) => {
					const nextProgress =
						typeof v === "function" ? v(state.buildProgress) : v;
					return {
						buildProgress: normalizeBuildProgress(nextProgress),
					};
				}),
			),
		setThermalInputs: (thermalInputs) => set({ thermalInputs }),
		setSiteConditions: (siteConditions) => set({ siteConditions }),
		setInteriorLayout: (interiorLayout) => set({ interiorLayout }),
		// Partial by design: the Karaktär panel commits one swatch at a time,
		// and merging here keeps that ONE `set` — i.e. one undo step per click
		// (see the one-drag-one-undo-step note).
		setFinishes: (finishes) =>
			set((state) => ({ finishes: { ...state.finishes, ...finishes } })),
		setWallHeightM: (wallHeightM) =>
			set({ wallHeightM: resolveWallHeightM(wallHeightM) }),
		setIndoorRoom: (indoorRoom) =>
			set({ indoorRoom: normalizeIndoorRoom(indoorRoom) }),
		setProjectName: (projectName) => set({ projectName }),
		setLocationMode: (locationMode) => set({ locationMode }),
		setClimateProfile: (climateProfile) => set({ climateProfile }),
		setPostalCode: (postalCode) => set({ postalCode }),
		hydrateFromDesign: (design) => {
			const mapped = fromDesignRow(design);
			set({
				market: mapped.market,
				locationMode: mapped.locationMode,
				climateProfile: mapped.climateProfile,
				dimensions: mapped.dimensions,
				partitionSplit: mapped.partitionSplit,
					twoRoomConfig: mapped.twoRoomConfig,
					timeOfDay: normalizeTimeOfDay(mapped.timeOfDay),
					rotationDeg: snapRotationDeg(mapped.rotationDeg),
					wizardCapacityHint: mapped.wizardCapacityHint,
					visibility: mapped.visibility,
				openings: withoutPanoramaWallWindows(
					mapped.openings,
					mapped.twoRoomConfig,
				),
				deckConfig: mapped.deckConfig,
				roofConfig: mapped.roofConfig,
				buildProgress: normalizeBuildProgress(mapped.buildProgress),
				thermalInputs: { ...DEFAULT_THERMAL, ...mapped.thermalInputs },
				siteConditions: {
					...DEFAULT_SITE_CONDITIONS,
					...mapped.siteConditions,
				},
				interiorLayout: mapped.interiorLayout,
				finishes: mapped.finishes,
				wallHeightM: mapped.wallHeightM,
				indoorRoom: mapped.indoorRoom,
				projectName: mapped.projectName,
				postalCode: mapped.postalCode,
				loadingDesign: false,
			});
		},
		loadTemplate: ({
			type,
			dimensions,
			partitionSplit,
				twoRoomConfig,
				timeOfDay,
				rotationDeg,
				openings,
			deckConfig,
			roofConfig,
			thermalInputs,
		}) => {
			set({
				dimensions,
				partitionSplit: partitionSplit ?? 0.5,
					twoRoomConfig: resolveTwoRoomConfig(twoRoomConfig),
					timeOfDay: normalizeTimeOfDay(timeOfDay),
					rotationDeg: snapRotationDeg(rotationDeg),
					// A pre-curated template is not the wizard's "N personer" promise,
					// even if its dimensions happen to match a preset.
					wizardCapacityHint: null,
					openings: withoutPanoramaWallWindows(
						openings,
						type === "tvarum" ? resolveTwoRoomConfig(twoRoomConfig) : null,
					),
				deckConfig,
				roofConfig: normalizeRoofConfig(type, roofConfig),
				thermalInputs: { ...DEFAULT_THERMAL, ...thermalInputs },
				visibility: DEFAULT_VIS,
				buildProgress: {},
				// Templates carry no authored interior; the generator owns it until
				// the user moves something.
				interiorLayout: null,
				// A template describes a shape, not a taste. Karaktärsval are
				// deliberately NOT reset here — someone who picked black slamfärg
				// and then tries a different footprint keeps their black facade.
			});
		},
		resetToDefaults: ({
			type,
			dimensions,
			partitionSplit: initialPartitionSplit,
				twoRoomConfig: initialTwoRoomConfig,
				timeOfDay: initialTimeOfDay,
				rotationDeg: initialRotationDeg,
				wizardCapacityHint: initialWizardCapacityHint,
			market,
			thermalInputs: initialThermal,
			siteConditions: initialSiteConditions,
			roofConfig: initialRoofConfig,
			locationMode,
			postalCode,
			climateProfile,
		}) => {
			const resolvedLocationMode =
				locationMode ?? (market === "intl" ? "manual_climate" : "postal_code");
			set({
				market,
				locationMode: resolvedLocationMode,
				climateProfile: climateProfile
					? { ...DEFAULT_INTL_CLIMATE_PROFILE, ...climateProfile }
					: DEFAULT_INTL_CLIMATE_PROFILE,
				dimensions,
				partitionSplit: initialPartitionSplit ?? 0.5,
					twoRoomConfig: resolveTwoRoomConfig(initialTwoRoomConfig),
					timeOfDay: normalizeTimeOfDay(initialTimeOfDay ?? DEFAULT_TIME_OF_DAY),
					rotationDeg: snapRotationDeg(
						initialRotationDeg ?? DEFAULT_SAUNA_ROTATION_DEG,
					),
					wizardCapacityHint: initialWizardCapacityHint ?? null,
				visibility: DEFAULT_VIS,
				openings: getDefaultOpenings(type, dimensions, {
					partitionSplit: initialPartitionSplit ?? 0.5,
					twoRoomConfig: initialTwoRoomConfig,
				}),
				deckConfig: null,
				roofConfig: normalizeRoofConfig(
					type,
					initialRoofConfig ?? DEFAULT_ROOF_CONFIG_BY_TYPE[type],
				),
				buildProgress: {},
				thermalInputs: { ...DEFAULT_THERMAL, ...initialThermal },
				siteConditions: {
					...DEFAULT_SITE_CONDITIONS,
					...initialSiteConditions,
				},
				// resetToDefaults is what fires on a sauna-type change. A layout
				// authored for a fyrkantig describes walls a tvårum's hot room does
				// not have, so trimming it would keep an arbitrary fragment; going
				// back to the generated default is the deterministic answer.
				interiorLayout: null,
				// Unlike `loadTemplate`, this IS a new design (it fires on a
				// sauna-type change), so the karaktärsval go back to the defaults
				// along with everything else.
				finishes: { ...DEFAULT_FINISHES },
				wallHeightM: null,
				indoorRoom: null,
				projectName: getT(market).rail_defaultProjectName,
				postalCode: postalCode ?? "",
				loadingDesign: false,
			});
		},
	};
}
